import type { Condition, OperatorId } from '@resource-kit/model';

/**
 * Client-side pagination kernel (#1392 D5).
 *
 * For a resource declaring `pagination: 'client'`, `ResourcePage` fetches the
 * whole capped row set once and then runs search / filter / sort / pagination
 * in memory over it. This module is that in-memory kernel — kept as a pure
 * function so the semantics are unit-testable without rendering. The semantics
 * mirror the backend list kernel where practical (eq/like/in/range ops,
 * substring search, field sort) so a resource can flip between `server` and
 * `client` modes without an operator-visible behavior change.
 */

/** Page-size cap for a single `client` fetch. A resource in client mode must
 *  fit under this bound; the endpoints this serves (Stocktake ≤100,
 *  PurchaseOrders ≤20, Users) all do. Documented so a resource that outgrows
 *  it flips back to `server` rather than silently truncating. */
export const CLIENT_PAGINATION_CAP = 1000;

export interface ClientQueryState {
  search?: string;
  conditions: readonly Condition[];
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  page: number;
  limit: number;
  /** Field keys eligible for free-text `search` (stringified + substring). */
  searchFields: readonly string[];
}

export interface ClientQueryResult<T> {
  /** The page slice to render. */
  rows: T[];
  /** Total rows AFTER search + filter (before pagination) — drives the footer. */
  total: number;
}

function cellString(value: unknown): string {
  if (value == null) return '';
  return String(value);
}

/** Numeric compare when both sides parse as finite numbers, else locale string
 *  compare. Used for lt/gt and for sort. */
function compare(a: unknown, b: unknown): number {
  const na = Number(a);
  const nb = Number(b);
  if (
    a != null &&
    b != null &&
    a !== '' &&
    b !== '' &&
    Number.isFinite(na) &&
    Number.isFinite(nb)
  ) {
    return na - nb;
  }
  return cellString(a).localeCompare(cellString(b));
}

function matchOp(cellValue: unknown, op: OperatorId, filterValue: unknown): boolean {
  const cell = cellString(cellValue);
  switch (op) {
    case 'eq':
      return cell === cellString(filterValue);
    case 'ne':
      return cell !== cellString(filterValue);
    case 'like':
    case 'ilike':
      return cell.toLowerCase().includes(cellString(filterValue).toLowerCase());
    case 'in': {
      const set = Array.isArray(filterValue)
        ? filterValue.map(cellString)
        : cellString(filterValue)
            .split(',')
            .map((s) => s.trim());
      return set.includes(cell);
    }
    case 'lt':
      return compare(cellValue, filterValue) < 0;
    case 'gt':
      return compare(cellValue, filterValue) > 0;
    case 'from':
      return compare(cellValue, filterValue) >= 0;
    case 'to':
      return compare(cellValue, filterValue) <= 0;
    case 'between': {
      const [a, b] = Array.isArray(filterValue) ? filterValue : [];
      return compare(cellValue, a) >= 0 && compare(cellValue, b) <= 0;
    }
    default:
      return true;
  }
}

function matchesConditions<T>(row: T, conditions: readonly Condition[]): boolean {
  // AND across conditions — same conjunction the backend kernel applies.
  return conditions.every((c) => {
    if (c.value === undefined || c.value === null || c.value === '') return true;
    return matchOp((row as Record<string, unknown>)[c.field], c.op, c.value);
  });
}

function matchesSearch<T>(row: T, search: string, searchFields: readonly string[]): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === '') return true;
  return searchFields.some((f) =>
    cellString((row as Record<string, unknown>)[f])
      .toLowerCase()
      .includes(needle),
  );
}

/**
 * Apply search → filter → sort → paginate over an in-memory row set. Pure: no
 * mutation of `rows`. Returns the current page slice plus the post-filter total
 * so the caller can render an accurate pagination footer.
 */
export function applyClientQuery<T>(
  rows: readonly T[],
  state: ClientQueryState,
): ClientQueryResult<T> {
  const {
    search = '',
    conditions,
    sortField,
    sortOrder = 'asc',
    page,
    limit,
    searchFields,
  } = state;

  const filtered = rows.filter(
    (row) => matchesSearch(row, search, searchFields) && matchesConditions(row, conditions),
  );

  const sorted = sortField
    ? [...filtered].sort((a, b) => {
        const dir = sortOrder === 'asc' ? 1 : -1;
        return (
          compare(
            (a as Record<string, unknown>)[sortField],
            (b as Record<string, unknown>)[sortField],
          ) * dir
        );
      })
    : filtered;

  const start = (page - 1) * limit;
  return { rows: sorted.slice(start, start + limit), total: sorted.length };
}
